import { z } from 'zod'

type ValidationResult<T> = { valid: true; data: T } | { valid: false; error: string }

export function isValidationError<T>(
  result: ValidationResult<T>
): result is { valid: false; error: string } {
  return !result.valid
}

export function validateWithZod<T>(schema: z.ZodType<T>, data: unknown): ValidationResult<T> {
  const result = schema.safeParse(data)
  if (result.success) {
    return { valid: true, data: result.data }
  }

  const firstError = result.error.issues[0]
  let errorMessage = firstError?.message || 'Validation failed'

  if (firstError?.code === z.ZodIssueCode.invalid_type && firstError?.path?.includes('id')) {
    errorMessage = 'Field "id" is required and must be a non-empty string'
  }

  return { valid: false, error: errorMessage }
}

validateWithZod.throwOnError =
  <E extends new (...args: any[]) => Error>(ErrorConstructor: E) =>
  <T>(schema: z.ZodType<T>, data: unknown): T => {
    const result = validateWithZod(schema, data)

    if (isValidationError(result)) {
      throw new ErrorConstructor(result.error)
    }

    return result.data
  }
